import { Buffer } from 'buffer'

import { DocumentNode } from 'graphql'
import {
  ClientError as BaseClientError,
  GraphQLClient as BaseGraphQLClient,
} from 'graphql-request'

import {
  CSR_HEADER_VALUE,
  REQUEST_SOURCE_HEADER,
} from '@bettermode/common/constants'

import { createEvent, TribeEvent, TribeEventObject } from '../lib/index.js'
import {
  ApiErrorCodes,
  ClientError,
  ErrorResponse,
  flattenErrors,
} from '../lib/response.js'
import { parseToken } from '../lib/token.js'

export type TribeClientOptions = {
  graphqlUrl?: string
  accessToken?: string
  clientId?: string
  clientSecret?: string
  fetch?: typeof fetch
  wsUrl?: string
  onAccessTokenExpired?: () => Promise<string | null>
  onError?: (
    errors: ErrorResponse[],
    client: GraphqlClient,
    error?: null | ClientError,
  ) => void
  onEvent?: (event: TribeEvent) => void
}

type RequestOptions = {
  query: DocumentNode
  variables?: Record<string, unknown>
  customToken?: string
  useBasicToken?: boolean
}

const EXPIRY_BUFFER_MS = 30_000
export class GraphqlClient extends BaseGraphQLClient {
  public accessToken?: string

  public graphqlUrl?: string

  private clientId?: string

  private clientSecret?: string

  private onAccessTokenExpired?: () => Promise<string | null>

  private onError?: TribeClientOptions['onError']

  private onEvent?: (event: TribeEvent) => void

  private refreshPromise: Promise<string | null> | null = null

  private tokenExpiryMs: number | null = null

  constructor(options: TribeClientOptions) {
    const {
      graphqlUrl = 'https://api.bettermode.com/graphql',
      accessToken,
      clientId,
      clientSecret,
      fetch,
      onAccessTokenExpired,
      onError,
      onEvent,
    } = options
    super(graphqlUrl, { fetch })

    this.graphqlUrl = graphqlUrl
    this.onAccessTokenExpired = onAccessTokenExpired
    this.onError = onError
    this.onEvent = onEvent
    this.clientId = clientId
    this.clientSecret = clientSecret

    this.setToken(accessToken)

    // for tracking the source of the request
    this.setHeader(REQUEST_SOURCE_HEADER, CSR_HEADER_VALUE)
  }

  private getBasicToken() {
    return Buffer.from(`${this.clientId}:${this.clientSecret}`).toString(
      'base64',
    )
  }

  private isTokenExpiredOrExpiring(): boolean {
    if (this.tokenExpiryMs === null) {
      /**
       * Either there has been no token set, or the token is permanent
       */
      return false
    }

    return this.tokenExpiryMs - EXPIRY_BUFFER_MS <= Date.now()
  }

  private isInvalidTokenError(error: BaseClientError): boolean {
    const errors = error?.response?.errors
    if (!Array.isArray(errors)) {
      return false
    }
    return errors.some(
      (e: ErrorResponse) => e?.code === ApiErrorCodes.INVALID_ACCESS_TOKEN,
    )
  }

  private async rotateToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    if (!this.onAccessTokenExpired) {
      return null
    }

    this.refreshPromise = (async () => {
      try {
        const newToken = await this.onAccessTokenExpired()
        if (newToken) {
          this.setToken(newToken)
          return newToken
        }
        return null
      } finally {
        this.refreshPromise = null
      }
    })()
    return this.refreshPromise
  }

  private setAuthHeader(options: RequestOptions): void {
    const { customToken = null, useBasicToken = false } = options
    if (useBasicToken) {
      if (customToken) {
        this.setHeader('Authorization', `Basic ${customToken}`)
      } else if (this.clientId && this.clientSecret) {
        this.setHeader('Authorization', `Basic ${this.getBasicToken()}`)
      }
    } else if (customToken || this.accessToken) {
      this.setHeader(
        'Authorization',
        `Bearer ${customToken || this.accessToken}`,
      )
    }
  }

  private usesInstanceToken(options: RequestOptions): boolean {
    return !options.useBasicToken && !options.customToken
  }

  private async executeRequest<T>(options: RequestOptions): Promise<T> {
    const { query, variables = {} } = options
    this.setAuthHeader(options)
    const data = await this.request<T>(query, variables)
    const event = createEvent(query, data as TribeEventObject, variables)
    if (event && typeof this.onEvent === 'function') {
      this.onEvent(event)
    }
    return data
  }

  async authorizedRequest<T>(options: RequestOptions): Promise<T> {
    const usesInstance = this.usesInstanceToken(options)

    if (
      usesInstance &&
      this.onAccessTokenExpired &&
      this.isTokenExpiredOrExpiring()
    ) {
      await this.rotateToken()
    }

    try {
      return await this.executeRequest<T>(options)
    } catch (error: unknown) {
      const clientError = error as BaseClientError
      const isTokenError =
        usesInstance &&
        this.onAccessTokenExpired &&
        this.isInvalidTokenError(clientError)

      if (isTokenError) {
        const newToken = await this.rotateToken()
        if (newToken) {
          return this.executeRequest<T>(options)
        }
      }

      if (clientError?.response?.errors) {
        const normalizedError: Array<ErrorResponse> = flattenErrors(
          clientError.response.errors,
        )
        clientError.response.errors = normalizedError
        this.onError?.(normalizedError, this, clientError as ClientError)
      }

      // eslint-disable-next-line no-throw-literal
      throw clientError as ClientError
    }
  }

  setToken(accessToken: string): void {
    this.accessToken = accessToken
    const parsed = parseToken(accessToken)
    this.tokenExpiryMs = parsed?.exp ? parsed.exp * 1000 : null
  }
}
